// Cache em memória do Saldo Devedor por emissão (payload de PU-diário, que já traz PU
// residual e quantidade em circulação por dia — ver saldoDevedorModel.js), pra não
// refazer o fetch a cada troca de período no Painel nem duplicar chamada quando o
// Detalhe2 abre uma emissão que o Painel já carregou.
//
// Janela de 7 meses (não 6): o Painel busca sempre ancorado no mês corrente REAL (não
// em m-1), pra cobrir tanto o "Hoje" da aba Carteira (precisa do PU do dia de acesso,
// que só existe se a janela incluir o mês corrente) quanto o m-6..m-1 usado pela
// Enquadramento — com a janela ancorada em m-1 (defaultPeriod), Hoje caía fora da
// janela buscada e `lastAtOrBefore` retrocedia pro último dia de m-1, mostrando o
// fechamento do mês passado em vez do dia de acesso.
import { postPUDiario } from './fluxoCaixaApi';

export const SALDO_MESES_MAX = 7;

const cache = new Map(); // emissaoId (string) -> { pu, dataFim }

// Promise em voo por `periodo:emissaoId` — evita bulk duplicado se dois consumidores
// chamarem getSaldoDevedorCached quase ao mesmo tempo antes da 1ª resposta chegar.
// Mesmo princípio de `_fullInFlight` em fluxoCaixaApi.js.
const _inFlight = new Map();

function subMonths(period, n) {
  let y = Math.floor(period / 100);
  let m = (period % 100) - n;
  while (m <= 0) { m += 12; y--; }
  return y * 100 + m;
}

// A janela buscada cobre `periodo` se ele cai dentro dos MESES_MAX meses já cacheados
// pra essa emissão — não depende de ter encontrado dado (PU/integralização podem
// legitimamente não existir num split sem, por exemplo, atualização recente).
function cobreJanela(entry, periodo) {
  if (!entry) return false;
  const alvo = parseInt(periodo, 10);
  return alvo <= entry.dataFim && alvo >= subMonths(entry.dataFim, SALDO_MESES_MAX - 1);
}

// Retorna { [emissaoId]: pu | null } — `pu` é o payload de postPUDiario pra essa emissão.
// Falha de rede em uma emissão não impede as outras — degrada pra null nela, igual ao
// resto do fluxo de pagamentos.
export async function getSaldoDevedorCached(emissaoIds, periodo, comoConvidado) {
  const dataFim = parseInt(periodo, 10);
  // Cache-key prefixada com "g:" pro preview de convidado (toggle "visão pública"
  // de Detalhe2.jsx) — namespace separado do cache real, senão um recorte vaza
  // pro outro ao alternar o toggle.
  const ck = id => (comoConvidado ? `g:${id}` : String(id));
  const key = id => `${periodo}:${ck(id)}`;
  // Cache é indexado por string (chave de objeto), mas o corpo enviado à API precisa dos
  // IDs como número — a API espera `emissao_ids` como lista de inteiros (ver ApiDocs) e
  // filtra/casa por tipo estrito no modo real_time=false; mandar string faz o backend não
  // achar o dado e devolver `historico: []` mesmo pra splits com PU já apurado.
  const need = emissaoIds.filter(id => !cobreJanela(cache.get(ck(id)), periodo));
  const toFetch = need.filter(id => !_inFlight.has(key(id)));

  if (toFetch.length) {
    const dataInicio = subMonths(dataFim, SALDO_MESES_MAX - 1);
    const promise = postPUDiario(toFetch.map(Number), dataInicio, dataFim, comoConvidado).catch(() => ({})).then(puMap => {
      toFetch.forEach(id => { cache.set(ck(id), { pu: puMap[id] || null, dataFim }); });
      return puMap;
    });
    toFetch.forEach(id => _inFlight.set(key(id), promise));
    promise.finally(() => { toFetch.forEach(id => _inFlight.delete(key(id))); });
  }

  const joining = [...new Set(need.filter(id => _inFlight.has(key(id))).map(id => _inFlight.get(key(id))))];
  if (joining.length) await Promise.all(joining);

  const out = {};
  emissaoIds.forEach(id => { out[id] = cache.get(ck(id))?.pu || null; });
  return out;
}

export function clearSaldoDevedorCache() { cache.clear(); _inFlight.clear(); }
